/**
 * Terms reported by the most recent consent read, shared by every
 * `useConsentApi` instance on the page.
 *
 * `POST /consent` neither takes nor returns a revision, so an acceptance
 * can only be recorded under the revision the read that raised the gate
 * reported. Those two steps run in different components: the entry
 * surfaces (`SidekickSuggestionsCard`, the dock) read the status, while
 * `SidekickConsentGate` records the acceptance. Each calls its own
 * `useConsentApi`, so a hook-local ref would leave the accepting
 * instance empty and the acceptance would never seed the cache.
 *
 * Deliberately in memory only. A page load re-reads consent live, and
 * the durable record is the API cache in `consentStorage`. Keyed by
 * profile id so a shared browser cannot hand one user's terms to
 * another.
 */

import type { SidekickConsentTerms } from '../contexts/SidekickApiContext';

const termsByUser = new Map<string, SidekickConsentTerms | undefined>();

/**
 * Records the terms a consent read reported for the given user. A
 * missing user id is a no-op, matching the API cache, which has nothing
 * to key on without one.
 *
 * @param userId - Profile id the read was made for.
 * @param terms - Terms the read reported, if any.
 */
export const rememberConsentTerms = (
  userId: string | undefined,
  terms?: SidekickConsentTerms
): void => {
  if (userId === undefined || userId.length === 0) {
    return;
  }

  termsByUser.set(userId, terms);
};

/**
 * Reads back the terms the most recent consent read reported for the
 * given user, or `undefined` when no read has happened yet.
 *
 * @param userId - Profile id to look up.
 * @returns The remembered terms, or `undefined`.
 */
export const recallConsentTerms = (
  userId?: string
): SidekickConsentTerms | undefined => {
  if (userId === undefined || userId.length === 0) {
    return undefined;
  }

  return termsByUser.get(userId);
};

/**
 * Drops every remembered read. Module state outlives a single React
 * tree, so suites that assert on seeding must start from empty.
 */
export const clearConsentTermsSession = (): void => {
  termsByUser.clear();
};
